import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useData } from "../../contexts/DataContext";
import { api } from "../../lib/api";
import { Bell, CheckCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { formatDistanceToNow } from "date-fns";

export default function NotificationsMenu() {
  const { tasks, notifications, refreshNotifications, setOpenTaskId } = useData();
  const [notifOpen, setNotifOpen] = useState(false);
  const navigate = useNavigate();
  const unread = notifications.filter((n) => !n.read).length;

  const openNotification = async (n) => {
    await api.post(`/notifications/${n.id}/read`).catch(() => {});
    refreshNotifications();
    setNotifOpen(false);
    if (n.link) {
      navigate(n.link);
    } else if (n.channel === "general") {
      navigate("/chat");
    } else if (n.task_id) {
      const task = tasks.find((t) => t.id === n.task_id);
      if (task) navigate(`/project/${task.project_id}?task=${task.id}`);
      else setOpenTaskId(n.task_id);
    }
  };

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    refreshNotifications();
  };

  return (
    <Popover open={notifOpen} onOpenChange={setNotifOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" className="relative h-8 w-8 flex items-center justify-center rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
          <Bell size={17} />
          {unread > 0 && (
            <span data-testid="notifications-unread-count" className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-0.5 rounded-full bg-[#E31E24] text-white text-[10px] font-bold flex items-center justify-center">{unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          <span className="text-sm font-semibold font-heading">Notifications</span>
          <button data-testid="mark-all-read" onClick={markAll} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
            <CheckCheck size={13} />Mark all read
          </button>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {notifications.length === 0 && <p className="text-sm text-muted-foreground p-4 text-center">No notifications yet</p>}
          {notifications.map((n) => (
            <button key={n.id} data-testid={`notification-${n.id}`} onClick={() => openNotification(n)}
              className={`w-full text-left px-3 py-2.5 border-b border-border last:border-0 hover:bg-muted transition-colors ${!n.read ? "bg-[#E31E24]/5" : ""}`}>
              <div className="flex items-start gap-2">
                {!n.read && <span className="h-2 w-2 rounded-full bg-[#E31E24] mt-1.5 shrink-0" />}
                <div className={!n.read ? "" : "pl-4"}>
                  <p className="text-sm font-medium leading-tight">{n.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{n.body}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">{formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
